import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRegisterCompanyMutation } from 'api';
import { RegisterCompanyFormValues } from 'features/companyRegistration/components/RegisterCompanyForm/hooks';
import { Links } from 'settings';
import { ApiErrors } from 'types';

import { registerRequestPayloadAdapter } from '../adapters';
import { registerCompanyDefaultValues } from './config';

interface LocationState {
  expired: boolean;
}

interface UseRegistrationPageLogicReturn {
  pageTitle: ReactNode;
  pageSubTitle: ReactNode;
  defaultValues: RegisterCompanyFormValues;
  handleRegisterSubmit: (data: RegisterCompanyFormValues) => Promise<void>;
  isLoading: boolean;
  errors?: Record<string, ApiErrors['errors']> | null;
}

export function useRegistrationPageLogic(): UseRegistrationPageLogicReturn {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();

  const { registerCompanyMutation, isLoading, errors } = useRegisterCompanyMutation({
    resend: false,
  });

  const repeatRegistration = (state as LocationState)?.expired;

  const pageTitle = t(
    repeatRegistration
      ? 'companyRegistration.title.repeatRegistration'
      : 'companyRegistration.title.registration',
  );
  const pageSubTitle = t(
    repeatRegistration
      ? 'companyRegistration.subTitle.repeatRegistration'
      : 'companyRegistration.subTitle.registration',
  );

  async function handleRegisterSubmit(data: RegisterCompanyFormValues) {
    const requestPayload = registerRequestPayloadAdapter(data);

    await registerCompanyMutation(requestPayload);
    navigate(Links.auth.registration.success, { state: { requestPayload } });
  }

  return {
    pageTitle,
    pageSubTitle,
    defaultValues: registerCompanyDefaultValues,
    handleRegisterSubmit,
    isLoading,
    errors,
  };
}
